/* import __COLOCATED_TEMPLATE__ from './combo-input.hbs'; */
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import { later } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import COUNTRY_ISO_CODES from '@square/ember-phone-number/utils/country-iso-codes';
import { getCountryCallingCode } from 'libphonenumber-js';
import { setInputAutocomplete, setInputEnterKeyHint, setInputEnterListener } from '../utils/input-util';
const DEFAULT_COUNTRY_CODE = 'US';
export const FIELD_MODE = {
  PHONE: 'phone',
  TEXT: 'text'
};
const ACTIONS = Object.freeze({
  COUNTRY_CODE_MANUAL_CHANGE: 'COUNTRY_CODE_MANUAL_CHANGE',
  // Todo: Once we have a way to detect when the country code is changed automatically, we should add this action
  COUNTRY_CODE_AUTOMATIC_CHANGE: 'COUNTRY_CODE_AUTOMATIC_CHANGE',
  FIELD_TYPE: 'FIELD_TYPE'
});
let PhoneNumberInputComponent = (_class = class PhoneNumberInputComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "fieldMode", _descriptor2, this);
    _initializerDefineProperty(this, "value", _descriptor3, this);
    _initializerDefineProperty(this, "selectedCountry", _descriptor4, this);
    _defineProperty(this, "marketInputElement", null);
    _defineProperty(this, "inputElement", null);
    _defineProperty(this, "handleSlottedInputInit", element => {
      this.inputElement = element;
      setInputEnterListener(element, this.args?.enterSubmit);
      setInputEnterKeyHint(element, 'go');
      setInputAutocomplete(element, this.args.autocomplete ?? 'username');
      if (this.args.setInput) {
        this.args.setInput(element);
      }
    });
    _defineProperty(this, "handleWrapperClick", () => {
      this.inputElement?.focus();
    });
    _defineProperty(this, "handleCountrySelect", event => {
      const newCountry = event.target.value;
      this.args.trackEvent?.(ACTIONS.COUNTRY_CODE_MANUAL_CHANGE, {
        elementIdentifier: 'mpui-combo-field',
        elementType: 'combo-input',
        previousValue: this.selectedCountry,
        newValue: newCountry
      });
      this.selectedCountry = newCountry;
      this.args.onChange?.(this.outputValue);
    });
    _defineProperty(this, "handleInputChange", event => {
      const inputValue = event.target.value;
      const existingFieldMode = this.fieldMode;
      this.updateFieldMode(inputValue);
      if (existingFieldMode !== this.fieldMode) {
        this.args.trackEvent?.(ACTIONS.FIELD_TYPE, {
          elementIdentifier: 'mpui-combo-field',
          elementType: 'combo-input',
          previousValue: existingFieldMode,
          newValue: this.fieldMode
        });
        this.args.onFieldModeChange?.(this.fieldMode);
      }
      this.value = inputValue;
      this.args.onChange?.(this.outputValue);
    });
    _defineProperty(this, "handleInputBlur", () => {
      this.marketInputElement?.removeAttribute('focused');
    });
    _defineProperty(this, "handleInputFocus", () => {
      this.marketInputElement?.setAttribute('focused', '');
    });
    _defineProperty(this, "handleMarketInputLoad", e => {
      const {
        target: marketInput
      } = e;
      const {
        input
      } = e.detail;
      this.marketInputElement = marketInput;
      // market-input will force focus on shadow input when setting the 'focused' attribute,
      // which will blur the real input.
      // Setting shadow input 'display = none' can make the shadow input un-focusable,
      // which will prevent Safari from shifting the cursor from real input to shadow input.
      input.style.display = 'none';
      if (this.args.autofocus) {
        later(() => {
          this.inputElement.focus();
        });
      }
    });
    _defineProperty(this, "handleAutofill", event => {
      if (event.animationName === 'market-input-autofill-start') {
        document.querySelector(this.inputIdentifier)?.setAttribute('focused', '');
      }
    });
  }
  get tPrefix() {
    return this.args.tPrefix ?? '';
  }
  get fieldInvalid() {
    return !!this.args.errorText;
  }
  get displayCountrySelector() {
    return this.fieldMode === FIELD_MODE.PHONE;
  }
  get selectedCountryFlagEmoji() {
    if (!this.selectedCountry) {
      return '';
    }
    return this.getCountryFlagEmoji(this.selectedCountry);
  }
  get selectedCountryCallingCode() {
    return getCountryCallingCode(this.selectedCountry);
  }
  get countryList() {
    const countryIsoCodes = this.args.includeCountries?.map(country => country.toUpperCase()) ?? COUNTRY_ISO_CODES;
    const countries = countryIsoCodes.map(isoCode => ({
      callingCode: getCountryCallingCode(isoCode),
      flag: this.getCountryFlagEmoji(isoCode),
      isoCode,
      label: this.intl.t(`${this.tPrefix}country_code.${isoCode}`)
    }));
    // return country list sort by label
    return countries.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      } else if (a.label > b.label) {
        return 1;
      } else {
        return 0;
      }
    });
  }
  get outputValue() {
    if (this.fieldMode === FIELD_MODE.PHONE) {
      const onlyDigitsInput = this.value.replace(/\D/g, '');
      return `+${this.selectedCountryCallingCode}${onlyDigitsInput}`;
    }
    return this.value;
  }
  getCountryFlagEmoji(countryCode) {
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }
  updateFieldMode(inputValue) {
    if (inputValue.length < 2 ||
    // the length is less than 2
    inputValue.match(/\+/i) !== null ||
    // string contains +
    inputValue.match(/@/i) !== null ||
    // string contains @
    inputValue.match(/[a-zA-Z]/i) !== null ||
    // there is letter in the string
    inputValue.match(/\d/i) === null // there is no digits in the string
    ) {
      this.fieldMode = FIELD_MODE.TEXT;
      return;
    }
    // there is any char ascii larger than 127
    for (const char of inputValue) {
      if (char.charCodeAt() > 127) {
        this.fieldMode = FIELD_MODE.TEXT;
        return;
      }
    }
    this.fieldMode = FIELD_MODE.PHONE;
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "fieldMode", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return FIELD_MODE.TEXT;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "value", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.value ?? '';
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "selectedCountry", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.defaultCountry?.toUpperCase() ?? DEFAULT_COUNTRY_CODE;
  }
})), _class);
export { PhoneNumberInputComponent as default };